import { hashSeed } from "./patterns"

// Room overlay coloring: every room with a product draws in that product's
// color — canvas fill/stroke, label dots, panel dots, rollup dots. The color
// is a product-level choice: `overlay_color` on the RoomProduct snapshot when
// the user picked one, else a stable palette default hashed from the catalog
// id (so existing data colors itself without a migration). Room *names* are
// deliberately not in that list — see labelFontSize below.

/** Content colors chosen to read over arbitrary plan linework in both themes. */
export const OVERLAY_PALETTE: { hex: string; name: string }[] = [
  { hex: "#1d5fd6", name: "Blue" },
  { hex: "#0a8f4e", name: "Green" },
  { hex: "#b45309", name: "Orange" },
  { hex: "#7c3aed", name: "Violet" },
  { hex: "#0f766e", name: "Teal" },
  { hex: "#be185d", name: "Magenta" },
  { hex: "#7f5539", name: "Brown" },
  { hex: "#475569", name: "Slate" },
]

/**
 * Room tint opacity, as a percentage of the product's ink. The default is
 * the tint every room drew with before the control existed, so an untouched
 * takeoff looks identical; the bounds keep a room from vanishing entirely or
 * hiding the plan linework underneath it.
 */
export const DEFAULT_OVERLAY_FILL = 16
export const MIN_OVERLAY_FILL = 4
export const MAX_OVERLAY_FILL = 45

/** Stable palette default for a catalog product id. */
export function defaultOverlayColor(productId: string): string {
  return OVERLAY_PALETTE[hashSeed(productId) % OVERLAY_PALETTE.length].hex
}

/** The ink a room's product draws with; null when there is no product. */
export function productInk(
  product: { id: string; overlay_color?: string | null } | null | undefined
): string | null {
  if (!product) return null
  return product.overlay_color ?? defaultOverlayColor(product.id)
}

/** The tint percentage a room's product fills with, clamped to the bounds. */
export function productFill(
  product: { overlay_fill?: number | null } | null | undefined
): number {
  const fill = product?.overlay_fill
  if (fill == null || !Number.isFinite(fill)) return DEFAULT_OVERLAY_FILL
  return Math.min(MAX_OVERLAY_FILL, Math.max(MIN_OVERLAY_FILL, fill))
}

// Room name sizing. Names are screen-constant, so zooming out shrinks the plan
// beneath a label that never moves — at low zoom a name ends up wider than the
// room it belongs to and lands on its neighbors. Rather than scale the text
// down until it's unreadable, it steps 15 -> 12 -> a dot at the centroid.
export const LABEL_SIZES = [15, 12] as const
export const LABEL_PAD_PX = 8 // horizontal breathing room to leave in the box
export const LABEL_DOT_R = 3.5 // the centroid marker, and the name's product dot
export const LABEL_MIN_SCALE = 0.35 // below this, names are noise: dots only
export const LABEL_MAX_W = 160 // the Text box width, and so the ellipsis point

/** Estimated on-screen width of a bold label — deliberately not measureText. */
export function labelWidth(name: string, fontSize: number): number {
  return Math.min(name.length * fontSize * 0.55, LABEL_MAX_W)
}

/**
 * Largest label size that fits the room's on-screen box, or null for none.
 * Fits = the name clears the box width with padding to spare, and the box is
 * tall enough that the name doesn't fill it wall to wall (~2.5 line heights).
 */
export function labelFontSize(name: string, boxW: number, boxH: number): number | null {
  for (const size of LABEL_SIZES) {
    if (labelWidth(name, size) < boxW - LABEL_PAD_PX && boxH > size * 2.5) return size
  }
  return null
}

/** "#rrggbb" → "rgba(r,g,b,a)" for translucent fills. */
export function withAlpha(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${alpha})`
}
